import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | failed | timeout
  const [data, setData] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (attempt >= MAX_ATTEMPTS) {
      setState("timeout");
      return;
    }
    try {
      const res = await axios.get(`${API}/donations/status/${sessionId}`);
      setData(res.data);
      if (res.data.payment_status === "paid") {
        setState("success");
        return;
      }
      if (res.data.status === "expired") {
        setState("failed");
        return;
      }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  const amount = data ? (data.amount_total / 100).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-5" data-testid="donate-success-page">
      <div className="max-w-md w-full rounded-2xl bg-white border border-slate-200 p-10 text-center shadow-sm">
        {state === "polling" && (
          <>
            <Loader2 size={52} className="text-teal-600 animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">Confirming your donation…</h1>
            <p className="mt-3 text-slate-600">Please wait while we verify your payment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 size={56} className="text-teal-600 mx-auto" data-testid="donate-success-icon" />
            <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-5">Thank you{data?.donor_name ? `, ${data.donor_name}` : ""}!</h1>
            <p className="mt-3 text-slate-600">
              Your donation{amount ? ` of $${amount} CAD` : ""} was received. Thank you for supporting a
              fresh voice for our schools.
            </p>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={56} className="text-slate-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-3 text-slate-600">
              {state === "timeout"
                ? "Your payment is taking a moment. If you were charged, you'll receive confirmation shortly."
                : "We couldn't confirm your donation. No charge may have been made — feel free to try again."}
            </p>
          </>
        )}
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white font-semibold px-6 py-3 hover:bg-teal-700 transition-colors"
          data-testid="donate-back-home"
        >
          <ArrowLeft size={18} /> Back to home
        </Link>
      </div>
    </div>
  );
}
